import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Calendar, Ban, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export function AvailabilityTab({ hotelId, hotelName }) {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadBlockedDates();
  }, [hotelId]);

  const loadBlockedDates = async () => {
    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/availability/blocked/${hotelId}`);
      const data = await response.json();
      setBlockedDates(data.blocked_dates || []);
    } catch (error) {
      console.error('Failed to load blocked dates:', error);
      toast.error('Failed to load blocked dates');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockDates = async () => {
    if (!startDate || !endDate) {
      toast.error('Please select both start and end dates');
      return;
    }

    try {
      setLoading(true);
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/availability/block`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          hotel_id: hotelId,
          start_date: startDate,
          end_date: endDate,
          reason: reason || 'Manual block by hotel manager',
          blocked_by: 'hotel_manager',
          source: 'hotel_dashboard'
        })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success(`Successfully blocked ${data.nights_blocked} night(s)`);
        setStartDate('');
        setEndDate('');
        setReason('');
        loadBlockedDates();
      } else {
        throw new Error(data.detail || 'Failed to block dates');
      }
    } catch (error) {
      console.error('Error blocking dates:', error);
      toast.error(error.message || 'Failed to block dates');
    } finally {
      setLoading(false);
    }
  };

  const handleUnblock = async (blockId) => {
    try {
      const backendUrl = BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/availability/unblock`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ block_id: blockId })
      });

      const data = await response.json();
      
      if (data.success) {
        toast.success('Date block removed');
        loadBlockedDates();
      } else {
        throw new Error(data.detail || 'Failed to unblock');
      }
    } catch (error) {
      console.error('Error unblocking:', error);
      toast.error('Failed to unblock dates');
    }
  };

  return (
    <div className="space-y-6" data-testid="owner-availability-tab">
      <Card className="border-primary/20 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 mt-1">
              <Calendar className="w-5 h-5 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-sm mb-1">Manage Availability Anywhere</h3>
              <p className="text-sm text-muted-foreground mb-2">
                Block dates from this dashboard, or use our <strong>Telegram bot</strong> to control availability from your phone!
              </p>
              <div className="text-xs text-muted-foreground space-y-1">
                <p>Quick commands from Telegram:</p>
                <code className="block bg-background/50 px-2 py-1 rounded mono">close sales {hotelId} May 20-25</code>
                <code className="block bg-background/50 px-2 py-1 rounded mono">status {hotelId}</code>
              </div>
              <a 
                href="/HOTEL_OWNER_TELEGRAM_GUIDE.md" 
                target="_blank"
                className="text-xs text-primary hover:underline mt-2 inline-block"
              >
                View Telegram Bot Setup Guide →
              </a>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Ban className="w-5 h-5 text-primary" />
            Block Dates
          </CardTitle>
          <p className="text-sm text-muted-foreground">Prevent new bookings for specific date ranges</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="owner-start-date">Start Date</Label>
              <Input
                id="owner-start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                data-testid="owner-availability-start-date"
              />
            </div>
            <div>
              <Label htmlFor="owner-end-date">End Date</Label>
              <Input
                id="owner-end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                data-testid="owner-availability-end-date"
              />
            </div>
          </div>

          <div>
            <Label htmlFor="owner-block-reason">Reason (optional)</Label>
            <Input
              id="owner-block-reason"
              placeholder="e.g., Maintenance, renovation, fully booked elsewhere"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              data-testid="owner-availability-reason-input"
            />
          </div>

          <Button 
            onClick={handleBlockDates}
            disabled={!startDate || !endDate || loading}
            className="w-full md:w-auto"
            data-testid="owner-availability-block-button"
          >
            <Ban className="w-4 h-4 mr-2" />
            {loading ? 'Blocking...' : 'Block Dates'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-primary" /> 
            Blocked Dates for {hotelName}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading && !blockedDates.length ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
            </div>
          ) : blockedDates.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No blocked dates</p>
              <p className="text-xs mt-1">All dates are currently available for bookings</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Start Date</TableHead>
                    <TableHead>End Date</TableHead>
                    <TableHead>Nights</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {blockedDates.map((block) => (
                    <TableRow key={block.id} data-testid={`owner-blocked-date-${block.id}`}>
                      <TableCell className="mono text-xs">{block.start_date}</TableCell>
                      <TableCell className="mono text-xs">{block.end_date}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{block.nights_blocked} nights</Badge>
                      </TableCell>
                      <TableCell className="text-sm max-w-xs truncate">{block.reason}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-[10px]">
                          {block.source === 'telegram' && '📱 '}
                          {block.source === 'email' && '📧 '}
                          {block.source === 'hotel_dashboard' && '💻 '}
                          {block.source}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {new Date(block.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleUnblock(block.id)}
                          data-testid={`owner-unblock-button-${block.id}`}
                        >
                          <Trash2 className="w-3 h-3 mr-1" />
                          Unblock
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
